import React, { useState } from "react";
import JsonModal from "./JsonModal";

const STATUS_STYLE = {
  idle: { border: "#ced4da", badge: "secondary", label: "Idle" },
  running: { border: "#0d6efd", badge: "primary", label: "Running..." },
  done: { border: "#198754", badge: "success", label: "Done" },
  error: { border: "#dc3545", badge: "danger", label: "Error" },
  stub: { border: "#adb5bd", badge: "secondary", label: "Not implemented yet" },
};

const preStyle = {
  background: "#f8f9fa",
  padding: "8px",
  borderRadius: "4px",
  maxHeight: "220px",
  overflow: "auto",
  fontSize: "12px",
  marginTop: "8px",
  marginBottom: 0,
};

const COMPACT_PREVIEW_HEIGHT = "360px";

const StepNode = ({
  number,
  title,
  description,
  status = "idle",
  onRun,
  runLabel = "Run step",
  result,
  error,
  disabledReason,
  renderPreview,
}) => {
  const style = STATUS_STYLE[status] || STATUS_STYLE.idle;
  const canRun = typeof onRun === "function" && !disabledReason && status !== "running";
  const hasResult = result !== undefined && result !== null;
  const [modalOpen, setModalOpen] = useState(false);
  const [view, setView] = useState("preview");
  // Long previews (e.g. a many-line invoice) scroll inside a fixed height
  // unless the operator switches to full height.
  const [compact, setCompact] = useState(true);

  return (
    <div
      className="card"
      style={{
        borderLeft: `4px solid ${style.border}`,
        minWidth: "260px",
        maxWidth: renderPreview ? "600px" : "320px",
        width: "100%",
      }}
    >
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <h6 className="card-subtitle text-muted mb-1">Step {number}</h6>
          <span className={`badge bg-${style.badge}`}>{style.label}</span>
        </div>
        <h6 className="card-title">{title}</h6>
        {description && <p className="card-text small text-muted mb-2">{description}</p>}

        {onRun && (
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={onRun}
            disabled={!canRun}
          >
            {status === "running" ? "Running..." : runLabel}
          </button>
        )}

        {disabledReason && <div className="form-text">{disabledReason}</div>}

        {error && <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small">{error}</div>}

        {hasResult && (
          <>
            <div className="d-flex justify-content-end align-items-center mt-2" style={{ gap: "6px" }}>
              {renderPreview && view === "preview" && (
                <div className="btn-group btn-group-sm" role="group" aria-label="Preview height">
                  <button
                    type="button"
                    className={`btn btn-outline-secondary py-0 px-2 ${compact ? "active" : ""}`}
                    style={{ fontSize: "11px" }}
                    onClick={() => setCompact(true)}
                  >
                    Compact
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary py-0 px-2 ${compact ? "" : "active"}`}
                    style={{ fontSize: "11px" }}
                    onClick={() => setCompact(false)}
                  >
                    Full
                  </button>
                </div>
              )}
              {renderPreview && (
                <div className="btn-group btn-group-sm" role="group">
                  <button
                    type="button"
                    className={`btn btn-outline-secondary py-0 px-2 ${view === "preview" ? "active" : ""}`}
                    style={{ fontSize: "11px" }}
                    onClick={() => setView("preview")}
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-secondary py-0 px-2 ${view === "json" ? "active" : ""}`}
                    style={{ fontSize: "11px" }}
                    onClick={() => setView("json")}
                  >
                    JSON
                  </button>
                </div>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2"
                style={{ fontSize: "11px" }}
                onClick={() => setModalOpen(true)}
              >
                View full JSON
              </button>
            </div>

            {renderPreview && view === "preview" ? (
              <div style={compact ? { maxHeight: COMPACT_PREVIEW_HEIGHT, overflowY: "auto" } : undefined}>{renderPreview(result)}</div>
            ) : (
              <pre style={preStyle}>{JSON.stringify(result, null, 2)}</pre>
            )}

            <JsonModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              title={`Step ${number} — ${title}`}
              data={result}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default StepNode;
